import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Snackbar,
  Alert,
  Rating,
  Divider,
} from '@mui/material';
import {
  Restaurant,
  RoomService,
  LocalLaundryService,
  Spa,
  Add,
  Visibility,
  Star,
  ShoppingCart,
} from '@mui/icons-material';

const serviceCategories = [
  {
    id: 1,
    name: 'Restaurant',
    icon: Restaurant,
    color: '#d32f2f',
    description: 'Fine dining and casual meals',
    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400',
    rating: 4.8,
    items: [
      { name: 'Breakfast Buffet', price: 25, description: 'Continental breakfast with fresh fruits' },
      { name: 'Lunch Special', price: 30, description: 'Chef\'s special of the day' },
      { name: 'Dinner Menu', price: 50, description: 'À la carte fine dining' },
      { name: 'Dessert Platter', price: 15, description: 'Assorted desserts' },
    ],
  },
  {
    id: 2,
    name: 'Room Service',
    icon: RoomService,
    color: '#1976d2',
    description: 'Order meals to your room',
    image: 'https://images.unsplash.com/photo-1559329007-40df8a9345d8?w=400',
    rating: 4.6,
    items: [
      { name: 'Continental Breakfast', price: 28, description: 'Delivered to your room' },
      { name: 'Snacks & Beverages', price: 15, description: 'Light refreshments' },
      { name: 'Dinner Tray', price: 45, description: 'Full course meal' },
      { name: 'Late Night Menu', price: 20, description: 'Available 24/7' },
    ],
  },
  {
    id: 3,
    name: 'Laundry',
    icon: LocalLaundryService,
    color: '#2e7d32',
    description: 'Professional laundry services',
    image: 'https://images.unsplash.com/photo-1517677208171-0bc6725a3e60?w=400',
    rating: 4.7,
    items: [
      { name: 'Regular Wash', price: 20, description: 'Standard cleaning' },
      { name: 'Express Service', price: 35, description: 'Same-day delivery' },
      { name: 'Dry Cleaning', price: 30, description: 'Professional dry clean' },
      { name: 'Iron & Press', price: 10, description: 'Pressing service only' },
    ],
  },
  {
    id: 4,
    name: 'Spa',
    icon: Spa,
    color: '#9c27b0',
    description: 'Relaxation and wellness treatments',
    image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400',
    rating: 4.9,
    items: [
      { name: 'Swedish Massage', price: 80, description: '60-minute full body massage' },
      { name: 'Facial Treatment', price: 70, description: 'Deep cleansing facial' },
      { name: 'Spa Package', price: 150, description: 'Massage + Facial + Manicure' },
      { name: 'Aromatherapy', price: 60, description: '45-minute relaxation session' },
    ],
  },
];

const myOrders = [
  {
    id: 'SO2026',
    service: 'Room Service',
    item: 'Continental Breakfast',
    date: '2025-11-15',
    time: '08:30 AM',
    amount: 28,
    status: 'Delivered',
    rating: 5,
  },
  {
    id: 'SO2025',
    service: 'Spa',
    item: 'Swedish Massage',
    date: '2025-11-14',
    time: '02:00 PM',
    amount: 80,
    status: 'Completed',
    rating: 5,
  },
  {
    id: 'SO2024',
    service: 'Laundry',
    item: 'Express Service',
    date: '2025-11-13',
    time: '10:00 AM',
    amount: 35,
    status: 'Processing',
    rating: null,
  },
];

export default function CustomerServices() {
  const [selectedService, setSelectedService] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orders, setOrders] = useState(myOrders);
  const [orderForm, setOrderForm] = useState({
    item: '',
    quantity: 1,
    specialRequests: '',
    deliveryTime: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setOrderDialogOpen(true);
  };

  const handleOrderSubmit = () => {
    if (!orderForm.item) {
      setSnackbar({
        open: true,
        message: 'Please select a service item',
        severity: 'error',
      });
      return;
    }

    const selectedItem = selectedService.items.find(item => item.name === orderForm.item);
    const newOrder = {
      id: `SO${2027 + orders.length}`,
      service: selectedService.name,
      item: orderForm.item,
      date: new Date().toISOString().split('T')[0],
      time: orderForm.deliveryTime || 'ASAP',
      amount: selectedItem.price * orderForm.quantity,
      status: 'Processing',
      rating: null,
      quantity: orderForm.quantity,
      specialRequests: orderForm.specialRequests,
    };

    setOrders([newOrder, ...orders]);
    setSnackbar({
      open: true,
      message: `Order ${newOrder.id} placed successfully!`,
      severity: 'success',
    });
    setOrderDialogOpen(false);
    setOrderForm({ item: '', quantity: 1, specialRequests: '', deliveryTime: '' });
  };

  const ServiceIcon = selectedService?.icon || Restaurant;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Hotel Services
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Explore and order from our premium hotel services
        </Typography>
      </Box>

      <Grid container spacing={3} mb={4}>
        {serviceCategories.map((service) => {
          const IconComponent = service.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={service.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
                onClick={() => handleServiceClick(service)}
              >
                <CardMedia
                  component="img"
                  height="140"
                  image={service.image}
                  alt={service.name}
                />
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Avatar sx={{ bgcolor: service.color, width: 40, height: 40 }}>
                      <IconComponent />
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="bold">
                        {service.name}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Star sx={{ fontSize: 16, color: '#ffa726' }} />
                        <Typography variant="caption" color="text.secondary">
                          {service.rating} Rating
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {service.description}
                  </Typography>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    startIcon={<ShoppingCart />}
                    sx={{ bgcolor: service.color, '&:hover': { bgcolor: service.color } }}
                  >
                    Order Now
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          My Service Orders
        </Typography>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell><strong>Order ID</strong></TableCell>
                <TableCell><strong>Service</strong></TableCell>
                <TableCell><strong>Item</strong></TableCell>
                <TableCell><strong>Date & Time</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Rating</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell><strong>{order.id}</strong></TableCell>
                  <TableCell>{order.service}</TableCell>
                  <TableCell>{order.item}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(order.date).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.time}
                    </Typography>
                  </TableCell>
                  <TableCell><strong>${order.amount}</strong></TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      size="small"
                      color={
                        order.status === 'Delivered' || order.status === 'Completed' ? 'success' : 
                        order.status === 'Processing' ? 'warning' : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {order.rating ? (
                      <Rating value={order.rating} size="small" readOnly />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Pending
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {selectedService && (
              <>
                <Avatar sx={{ bgcolor: selectedService.color }}>
                  <ServiceIcon />
                </Avatar>
                <Typography variant="h6">Order {selectedService.name}</Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedService && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                select
                label="Select Item"
                value={orderForm.item}
                onChange={(e) => setOrderForm({ ...orderForm, item: e.target.value })}
                margin="normal"
                required
              >
                {selectedService.items.map((item) => (
                  <MenuItem key={item.name} value={item.name}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        ${item.price}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                type="number"
                label="Quantity"
                value={orderForm.quantity}
                onChange={(e) => setOrderForm({ ...orderForm, quantity: parseInt(e.target.value) || 1 })}
                margin="normal"
                inputProps={{ min: 1, max: 10 }}
              />

              <TextField
                fullWidth
                type="time"
                label="Preferred Delivery Time (Optional)"
                value={orderForm.deliveryTime}
                onChange={(e) => setOrderForm({ ...orderForm, deliveryTime: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Special Requests (Optional)"
                value={orderForm.specialRequests}
                onChange={(e) => setOrderForm({ ...orderForm, specialRequests: e.target.value })}
                margin="normal"
                placeholder="Any special instructions..."
              />

              {orderForm.item && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Total Amount:</Typography>
                    <Typography variant="h6" color="primary">
                      ${(selectedService.items.find(i => i.name === orderForm.item)?.price || 0) * orderForm.quantity}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleOrderSubmit} startIcon={<Add />}>
            Place Order
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
